// @figma file=pkCGby54zXzTsccXvHqzO6 node=15455:46578 name="Dailog" set="my-taible-shadcn-studio" verified=2026-03-22

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Dialog (root) ────────────────────────────────────────────────────────────

/**
 * General-purpose modal overlay with focus trap and scroll lock built on
 * Radix DialogPrimitive.
 *
 * Built on `@radix-ui/react-dialog` with full ARIA compliance:
 * - Role: `dialog`
 * - Focus trap: focus is captured inside while open
 * - Keyboard: `Escape` closes and returns focus to the trigger
 * - ARIA: `aria-labelledby` links the title; `aria-describedby` links the description
 * - Scroll lock: body scroll is locked while the modal is open
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button>Edit profile</Button>
 *   </DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Edit profile</DialogTitle>
 *       <DialogDescription>
 *         Make changes to your profile here. Click save when you're done.
 *       </DialogDescription>
 *     </DialogHeader>
 *     <DialogFooter>
 *       <Button type="submit">Save changes</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */

export interface DialogProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {}

const Dialog = DialogPrimitive.Root;
Dialog.displayName = 'Dialog';

// ─── DialogTrigger ────────────────────────────────────────────────────────────

export interface DialogTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger> {}

const DialogTrigger = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Trigger>,
  DialogTriggerProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Trigger ref={ref} className={cn(className)} {...props} />
));
DialogTrigger.displayName = 'DialogTrigger';

// ─── DialogPortal ─────────────────────────────────────────────────────────────

export interface DialogPortalProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal> {}

const DialogPortal = DialogPrimitive.Portal;
DialogPortal.displayName = 'DialogPortal';

// ─── DialogOverlay ────────────────────────────────────────────────────────────

export interface DialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> {}

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Backdrop fill — canvas token adapts to dark/light mode
      'fixed inset-0 z-50 bg-canvas/80 backdrop-blur-sm',
      // Entry/exit animations driven by Radix data-state
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

// ─── DialogClose ──────────────────────────────────────────────────────────────

export interface DialogCloseProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> {}

const DialogClose = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Close>,
  DialogCloseProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close ref={ref} className={cn(className)} {...props} />
));
DialogClose.displayName = 'DialogClose';

// ─── DialogContent ────────────────────────────────────────────────────────────

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {}

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Positioning — centred via translate
        'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
        // Layout
        'flex w-full max-w-lg flex-col gap-4',
        // Surface — card token so dialog floats above the page background in both modes
        'bg-card text-card-foreground rounded-[var(--card-radius,var(--radius))] border border-border',
        // Spacing — matches Figma 24px padding
        'p-6',
        // Elevation — matches Figma Light/box-shadow/default/lg
        'shadow-lg',
        // Focus ring
        'focus:outline-none',
        // Entry/exit animations
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'duration-200',
        className,
      )}
      {...props}
    >
      {children}
      {/* X close button — top-right corner, matches Figma Lucide/x placement */}
      <DialogPrimitive.Close
        className={cn(
          'absolute right-4 top-4',
          'rounded-sm text-muted-foreground',
          'opacity-70 transition-opacity',
          'hover:opacity-100',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:pointer-events-none',
        )}
        aria-label="Close"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

// ─── DialogHeader ─────────────────────────────────────────────────────────────

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 text-left', className)} {...props} />
  ),
);
DialogHeader.displayName = 'DialogHeader';

// ─── DialogFooter ─────────────────────────────────────────────────────────────

export interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Row layout, right-aligned — matches Figma modal footer pattern
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  ),
);
DialogFooter.displayName = 'DialogFooter';

// ─── DialogTitle ──────────────────────────────────────────────────────────────

export interface DialogTitleProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> {}

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      // Figma: Tailwind/LG/Semi Bold
      'text-lg font-semibold leading-snug text-foreground',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

// ─── DialogDescription ────────────────────────────────────────────────────────

export interface DialogDescriptionProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> {}

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      // Figma: Tailwind/SM/Regular
      'text-sm text-muted-foreground',
      className,
    )}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
